import React, { useState } from 'react';
import { Eye, ShoppingBag, Star, X } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

const Collections = () => {
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [wishlist, setWishlist] = useState([]);
  const [toastMessage, setToastMessage] = useState(null);

  const toggleWishlist = (product, e) => {
    if (e) e.stopPropagation();
    setWishlist(prev => {
      const isSaved = prev.some(item => item.id === product.id);
      const newWishlist = isSaved
        ? prev.filter(item => item.id !== product.id)
        : [...prev, product];

      showToast(isSaved ? 'Removed from cart' : 'Added to cart');
      return newWishlist;
    });
  };

  const showToast = (message) => {
    setToastMessage(message);
    setTimeout(() => {
      setToastMessage(null);
    }, 3000);
  };

  const openModal = (product) => {
    setSelectedProduct(product);
    setIsModalOpen(true);
    document.body.style.overflow = 'hidden';
  };

  const closeModal = () => {
    setIsModalOpen(false);
    document.body.style.overflow = 'unset';
  };

  const categories = [
    { id: 'all', name: 'All Collections' },
    { id: 'ethnic', name: 'Ethnic Wear' },
    { id: 'silk', name: 'Silk & Pattu' },
    { id: 'embroidery', name: 'Embroidery' },
    { id: 'kids', name: 'Kids Collection' },
    { id: 'western', name: 'Western Fusion' }
  ];

  const collections = [
    {
      id: 1,
      name: 'Royal Silk Saree',
      category: 'silk',
      image: '/Royal Silk Saree.jpg',
      rating: 4.8,
      reviews: 124,
      badge: 'New'
    },
    {
      id: 2,
      name: 'Embroidered Lehenga',
      category: 'embroidery',
      image: '/Embroidered Lehenga.jpg',
      rating: 4.9,
      reviews: 89,
      badge: 'Popular'
    },
    {
      id: 3,
      name: 'Traditional Pattu Pavadai',
      category: 'ethnic',
      image: '/Traditional Pattu Pavadai.jpg',
      rating: 4.7,
      reviews: 56,
      badge: 'Sale'
    },
    {
      id: 4,
      name: 'Kids Ethnic Set',
      category: 'kids',
      image: '/Kids Ethnic Set.jpg',
      rating: 4.6,
      reviews: 78,
      badge: 'Exclusive'
    },
    {
      id: 5,
      name: 'Fusion Gown',
      category: 'western',
      image: '/Fusion Gown.jpg',
      rating: 4.8,
      reviews: 92,
      badge: 'Trending'
    },
    {
      id: 6,
      name: 'Designer Salwar Kameez',
      category: 'ethnic',
      image: '/Designer Salwar Kameez.jpg',
      rating: 4.7,
      reviews: 103
    }
  ];

  const filteredCollections = selectedCategory === 'all'
    ? collections
    : collections.filter(item => item.category === selectedCategory);

  return (
    <>
      <section id="collections" className="py-16 md:py-20 bg-white overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Section Header */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-12 md:mb-16"
          >
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-4 px-4 tracking-tight">
              What We Offer
            </h2>
            <div className="w-20 md:w-24 h-1.5 bg-gradient-to-r from-brand-rose to-brand-pink mx-auto mb-6 rounded-full"></div>
            <p className="text-base md:text-lg text-gray-600 max-w-3xl mx-auto px-4 leading-relaxed font-medium">
              Discover our curated collection of premium clothing that blends classic aesthetics with contemporary fashion sensibilities
            </p>
          </motion.div>

          {/* Category Filter - Scrollable on Mobile */}
          <motion.div
            initial={{ opacity: 0, y: 15 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="flex overflow-x-auto pb-6 mb-8 md:mb-12 no-scrollbar scroll-smooth"
          >
            <div className="flex flex-nowrap md:flex-wrap md:justify-center gap-3 md:gap-5 px-4 mx-auto">
              {categories.map((category) => (
                <button
                  key={category.id}
                  onClick={() => setSelectedCategory(category.id)}
                  className={`flex-none px-6 md:px-8 py-3 md:py-3.5 rounded-full text-sm md:text-base font-bold transition-all duration-500 whitespace-nowrap shadow-sm ${selectedCategory === category.id
                    ? 'bg-gradient-to-r from-brand-rose to-brand-dusty text-white shadow-xl shadow-brand-rose/25 transform scale-105'
                    : 'bg-white text-gray-600 hover:bg-brand-cream border border-brand-pink/20'
                    }`}
                >
                  {category.name}
                </button>
              ))}
            </div>
          </motion.div>

          {/* Collections Grid */}
          <motion.div
            className="grid sm:grid-cols-2 lg:grid-cols-3 gap-8 md:gap-10 mb-20 px-2 sm:px-0"
          >
            {filteredCollections.map((collection, index) => (
              <motion.div
                key={collection.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="group relative bg-white rounded-2xl shadow-lg shadow-brand-rose/5 overflow-hidden hover:shadow-2xl hover:shadow-brand-rose/10 transition-all duration-500 transform hover:-translate-y-2 border border-brand-pink/10"
              >
                {/* Badge */}
                {collection.badge && (
                  <div className="absolute top-4 left-4 z-10">
                    <span className={`px-3 py-1.5 rounded-lg text-[10px] md:text-xs font-bold uppercase tracking-widest shadow-lg backdrop-blur-md ${collection.badge === 'New' ? 'bg-green-500/90 text-white' :
                      collection.badge === 'Sale' ? 'bg-red-500/90 text-white' :
                        collection.badge === 'Bestseller' ? 'bg-brand-rose shadow-brand-rose/20 text-white' :
                          'bg-brand-pink shadow-brand-pink/20 text-brand-dusty'
                      }`}>
                      {collection.badge}
                    </span>
                  </div>
                )}

                {/* Product Image */}
                <div className="relative overflow-hidden h-72 sm:h-80 md:h-96">
                  <img
                    src={collection.image}
                    alt={collection.name}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-1000"
                  />
                  <div className="absolute inset-0 bg-brand-rose/0 group-hover:bg-brand-rose/20 transition-all duration-500"></div>

                  {/* Quick Actions */}
                  <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all duration-500 gap-4">
                    <button
                      onClick={() => openModal(collection)}
                      className="bg-white/95 backdrop-blur-sm p-3.5 rounded-xl shadow-xl hover:bg-brand-rose hover:text-white transition-all duration-300 text-brand-rose"
                      aria-label="View Details"
                    >
                      <Eye size={20} />
                    </button>
                    <button
                      onClick={(e) => toggleWishlist(collection, e)}
                      className={`bg-white/95 backdrop-blur-sm p-3.5 rounded-xl shadow-xl hover:bg-brand-rose hover:text-white transition-all duration-300 ${wishlist.some(item => item.id === collection.id) ? 'text-brand-rose bg-brand-rose/10' : 'text-gray-400 hover:text-white'}`}
                      aria-label={wishlist.some(item => item.id === collection.id) ? "Remove from Cart" : "Add to Cart"}
                    >
                      <ShoppingBag size={20} className={wishlist.some(item => item.id === collection.id) ? "fill-brand-rose text-brand-rose" : ""} />
                    </button>
                  </div>
                </div>

                {/* Product Info */}
                <div className="p-6 md:p-8">
                  <h3 className="text-xl md:text-2xl font-bold text-gray-900 mb-2 group-hover:text-brand-rose transition-colors duration-300">{collection.name}</h3>

                  {/* Rating */}
                  <div className="flex items-center gap-2 mb-4">
                    <div className="flex items-center gap-0.5">
                      {[...Array(5)].map((_, i) => (
                        <Star
                          key={i}
                          size={16}
                          className={`${i < Math.floor(collection.rating)
                            ? 'fill-brand-gold text-brand-gold'
                            : 'text-gray-200'
                            }`}
                        />
                      ))}
                    </div>
                    <span className="text-[10px] md:text-sm text-gray-500 font-medium">
                      {collection.rating} ({collection.reviews} reviews)
                    </span>
                  </div>

                  <div className="pt-4 border-t border-brand-pink/10">
                    <button
                      onClick={() => openModal(collection)}
                      className="w-full bg-brand-cream text-brand-rose px-5 py-2.5 rounded-xl text-xs md:text-sm font-bold hover:bg-brand-rose hover:text-white transition-all duration-500 border border-brand-pink/20 shadow-sm"
                    >
                      View Details
                    </button>
                  </div>
                </div>
              </motion.div>
            ))}
          </motion.div>

          {/* CTA Section */}
          <div className="text-center bg-brand-cream/50 rounded-3xl p-10 md:p-16 border border-brand-pink/20 shadow-xl shadow-brand-rose/5 relative overflow-hidden">
            <div className="absolute top-0 right-0 w-64 h-64 bg-brand-pink/10 rounded-full -mr-32 -mt-32 blur-3xl"></div>
            <div className="absolute bottom-0 left-0 w-64 h-64 bg-brand-rose/10 rounded-full -ml-32 -mb-32 blur-3xl"></div>

            <h3 className="text-2xl md:text-4xl font-bold text-gray-900 mb-6 px-4 relative z-10">
              Can&apos;t find what you&apos;re looking for?
            </h3>
            <p className="text-base md:text-xl text-gray-600 mb-10 max-w-2xl mx-auto px-4 relative z-10 leading-relaxed font-medium">
              We offer bespoke styling services and custom designs tailored to your unique preferences and occasions
            </p>
            <div className="flex flex-col sm:flex-row gap-5 justify-center px-4 relative z-10">
              <a href="#contact" className="w-full sm:w-auto bg-gradient-to-r from-brand-rose to-brand-dusty text-white px-10 py-4 rounded-full font-bold hover:from-brand-dusty hover:to-brand-rose transition-all duration-500 shadow-xl shadow-brand-rose/30 transform hover:scale-105 active:scale-95 text-center block sm:inline-block">
                Request Custom Design
              </a>
              <a
                href="https://wa.me/447721540241"
                target="_blank"
                rel="noopener noreferrer"
                className="w-full sm:w-auto bg-green-500 hover:bg-green-600 text-white px-10 py-4 rounded-full font-bold transition-all duration-300 shadow-xl shadow-green-500/30 flex items-center justify-center gap-3 transform hover:scale-105 active:scale-95"
              >
                <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.149-.67.149-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.074-.297-.149-1.255-.462-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.297-.347.446-.521.151-.172.2-.296.3-.495.099-.198.05-.372-.025-.521-.075-.148-.669-1.611-.916-2.206-.242-.579-.487-.501-.669-.51l-.57-.01c-.198 0-.52.074-.792.372s-1.04 1.016-1.04 2.479 1.065 2.876 1.213 3.074c.149.198 2.095 3.2 5.076 4.487.709.306 1.263.489 1.694.626.712.226 1.36.194 1.872.118.571-.085 1.758-.719 2.006-1.413.248-.695.248-1.29.173-1.414-.074-.123-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z" />
                </svg>
                Get Styling Advice
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Product Quick View Modal */}
      <AnimatePresence>
        {isModalOpen && selectedProduct && (
          <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={closeModal}
              className="absolute inset-0 bg-black/60 backdrop-blur-sm"
            />
            <motion.div
              initial={{ opacity: 0, scale: 0.9, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.9, y: 20 }}
              className="relative bg-white w-full max-w-4xl rounded-3xl overflow-hidden shadow-2xl flex flex-col md:flex-row max-h-[90vh] overflow-y-auto"
            >
              <button
                onClick={closeModal}
                className="absolute top-4 right-4 z-10 bg-white/80 backdrop-blur-md p-2 rounded-full hover:bg-brand-rose hover:text-white transition-all duration-300"
              >
                <X size={20} />
              </button>

              {/* Modal Image */}
              <div className="md:w-1/2 h-64 md:h-auto">
                <img
                  src={selectedProduct.image}
                  alt={selectedProduct.name}
                  className="w-full h-full object-cover"
                />
              </div>

              {/* Modal Content */}
              <div className="md:w-1/2 p-8 md:p-12 flex flex-col">
                <div className="mb-2">
                  <span className="px-3 py-1 bg-brand-cream text-brand-rose text-xs font-bold uppercase tracking-widest rounded-lg">
                    {selectedProduct.category}
                  </span>
                </div>
                <h3 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">{selectedProduct.name}</h3>

                <div className="flex items-center gap-2 mb-6">
                  <div className="flex items-center gap-0.5">
                    {[...Array(5)].map((_, i) => (
                      <Star
                        key={i}
                        size={18}
                        className={`${i < Math.floor(selectedProduct.rating)
                          ? 'fill-brand-gold text-brand-gold'
                          : 'text-gray-200'
                          }`}
                      />
                    ))}
                  </div>
                  <span className="text-sm text-gray-500 font-medium">
                    {selectedProduct.rating} ({selectedProduct.reviews} reviews)
                  </span>
                </div>



                <p className="text-gray-600 mb-8 leading-relaxed">
                  Experience the perfect blend of traditional craftsmanship and contemporary elegance with our {selectedProduct.name}.
                  Expertly crafted using premium materials, this piece is designed to make you feel confident and stylish for any occasion.
                </p>

                <div className="mt-auto space-y-4">
                  <button
                    onClick={() => toggleWishlist(selectedProduct)}
                    className={`w-full py-4 rounded-xl font-bold transition-all duration-300 border block text-center flex items-center justify-center gap-2 ${wishlist.some(item => item.id === selectedProduct.id)
                      ? 'bg-brand-rose text-white border-transparent'
                      : 'bg-brand-cream text-brand-rose border-brand-pink/20 hover:bg-brand-rose hover:text-white'
                      }`}
                  >
                    <ShoppingBag size={20} className={wishlist.some(item => item.id === selectedProduct.id) ? "fill-white text-white" : ""} />
                    {wishlist.some(item => item.id === selectedProduct.id) ? 'Saved to Cart' : 'Add to Cart'}
                  </button>
                  <a
                    href={`https://wa.me/447721540241?text=${encodeURIComponent("Hi, I'm interested in the " + selectedProduct.name)}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-full bg-green-500 hover:bg-green-600 text-white py-4 rounded-xl font-bold transition-all duration-300 flex items-center justify-center gap-3"
                  >
                    Inquire on WhatsApp
                  </a>
                </div>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* Toast Notification */}
      <AnimatePresence>
        {toastMessage && (
          <motion.div
            initial={{ opacity: 0, y: 50, scale: 0.9 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, scale: 0.9, y: 20 }}
            className={`fixed ${wishlist.length > 0 ? 'bottom-32' : 'bottom-24'} left-1/2 transform -translate-x-1/2 z-[60] bg-gray-900 text-white px-6 py-3 rounded-full shadow-2xl flex items-center gap-3 transition-all duration-300`}
          >
            <ShoppingBag size={16} className="fill-brand-rose text-brand-rose" />
            <span className="font-medium text-sm">{toastMessage}</span>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Floating Checkout Bar */}
      <AnimatePresence>
        {wishlist.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 100 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 100 }}
            className="fixed bottom-0 left-0 right-0 z-50 bg-white border-t border-brand-pink/20 shadow-[0_-10px_40px_rgba(0,0,0,0.1)] p-4 md:p-6"
          >
            <div className="max-w-7xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4">
              <div className="flex items-center gap-4 w-full sm:w-auto">
                <div className="bg-brand-rose/10 w-12 h-12 rounded-full flex items-center justify-center shrink-0 hidden sm:flex">
                  <ShoppingBag className="text-brand-rose w-6 h-6" />
                </div>
                <div className="text-center sm:text-left w-full sm:w-auto">
                  <p className="text-gray-500 text-xs sm:text-sm font-medium">Your Selection</p>
                  <p className="text-base sm:text-xl font-bold text-gray-900">{wishlist.length} {wishlist.length === 1 ? 'Item' : 'Items'} Ready</p>
                </div>
              </div>

              <div className="flex flex-row items-center gap-2 sm:gap-3 w-full sm:w-auto">
                <button
                  onClick={() => setWishlist([])}
                  className="bg-gray-100 hover:bg-gray-200 text-gray-600 px-4 py-3 sm:py-3.5 rounded-xl font-bold transition-colors text-xs sm:text-sm"
                >
                  Clear
                </button>
                <a
                  href={`https://wa.me/447721540241?text=${encodeURIComponent(
                    "Hi, I would like to order the following items from your collection:\n\n" +
                    wishlist.map((item, index) => `${index + 1}. ${item.name}`).join("\n") +
                    "\n\nCould you please let me know the total price and details?"
                  )}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-full sm:w-auto bg-gradient-to-r from-brand-rose to-brand-dusty text-white px-6 sm:px-8 py-3 sm:py-3.5 rounded-xl font-bold shadow-xl shadow-brand-rose/30 hover:from-brand-dusty hover:to-brand-rose transition-all duration-300 flex items-center justify-center gap-2 transform hover:scale-105 active:scale-95 text-xs sm:text-base whitespace-nowrap"
                >
                  <svg className="w-4 h-4 sm:w-5 sm:h-5 fill-current shrink-0" viewBox="0 0 24 24">
                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.149-.67.149-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.074-.297-.149-1.255-.462-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.297-.347.446-.521.151-.172.2-.296.3-.495.099-.198.05-.372-.025-.521-.075-.148-.669-1.611-.916-2.206-.242-.579-.487-.501-.669-.51l-.57-.01c-.198 0-.52.074-.792.372s-1.04 1.016-1.04 2.479 1.065 2.876 1.213 3.074c.149.198 2.095 3.2 5.076 4.487.709.306 1.263.489 1.694.626.712.226 1.36.194 1.872.118.571-.085 1.758-.719 2.006-1.413.248-.695.248-1.29.173-1.414-.074-.123-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z" />
                  </svg>
                  Order on WhatsApp
                </a>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default Collections;
